import { isBotPServed, PServed } from '@wui/im'
import { v4 as uuid } from 'uuid'

import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'


import { useCurrentUser } from '@/shared/composables/current-user'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'
import { isSystemContactPServed } from '@/shared/utils/constants'
import { getSystemBotContact, resolveBotContact } from '@/shared/utils/contacts'

export function applyBotIdentityToRooms (pServed: string, name: string) {
  if (!pServed || !name) {
    return
  }

  const storeRooms = useStoreRooms()
  const room = storeRooms.findByPServed(pServed)
  if (room && !room.isGroup && room.name !== name) {
    storeRooms.updateRoom(room.id, { name })
  }

  storeRooms.rooms.forEach((item) => {
    item.subscribers.forEach((subscriber) => {
      if (subscriber.imLogin === pServed && subscriber.name !== name) {
        subscriber.name = name
      }
    })
  })
}

export function createPrivateRoom (roomId: string, contact: {
  imLogin: string
  internalNumber: string
  name: string
  id: PServed
  groupIds: Array<string>
}): Room {
  if (isSystemContactPServed(roomId) || isSystemContactPServed(contact.imLogin)) {
    return {
      id: roomId,
      pServedUser: roomId,
      isGroup: false,
      name: contact.name,
      number: contact.internalNumber,
      subscribers: new Map(),
      isEnabled: false,
    }
  }

  const { findByPServed, internalNumberContactMap, upsertBotContact, setContactIfDoesNotExists } = useStoreContacts()
  const subscribers = new Map()
  const resolvedContact = getSystemBotContact(roomId) ?? getSystemBotContact(contact.imLogin) ?? contact
  const storeContact = resolvedContact.imLogin ? findByPServed(resolvedContact.imLogin) : internalNumberContactMap[resolvedContact.internalNumber] // Для внешнего контакта imLogin = ""

  if (storeContact) {
    subscribers.set(uuid(), storeContact)
  } else {
    subscribers.set(uuid(), resolvedContact)
  }

  if (isBotPServed(roomId) || isBotPServed(resolvedContact.imLogin)) {
    const botContact = resolveBotContact(resolvedContact.imLogin || roomId, resolvedContact.name)
    if (typeof upsertBotContact === 'function') {
      upsertBotContact(botContact)
    } else {
      setContactIfDoesNotExists(botContact)
    }
  }

  const room: Room = {
    id: roomId,
    pServedUser: roomId,
    isGroup: false,
    name: resolvedContact.name ?? storeContact?.name,
    number: resolvedContact.internalNumber,
    subscribers,
    isEnabled: true,
    isExternal: !storeContact?.imLogin && !resolvedContact.imLogin,
  }
  const storeRooms = useStoreRooms()
  storeRooms.createRoom(room)
  return room
}

export function convertNumbersToSubscribersMap (subscribersNumbers: string[]): Map<string, Contact> {
  const { currentUser } = useCurrentUser()
  const storeContacts = useStoreContacts()
  const subscribers = new Map<string, Contact>()
  if (currentUser.value?.id) {
    subscribers.set(currentUser.value.id, currentUser.value as Contact)
  }
  return subscribersNumbers.reduce((acc, phoneNumber) => {
    const contact = storeContacts.findByInternalNumber(phoneNumber)
    if (contact) {
      acc.set(contact.id, contact)
    }
    return acc
  }, subscribers)
}

export function convertNumbersToSubscribersMapWithCurrentSubscriber (subscribersNumbers: string[]): Map<string, Contact> {
  const subscribers = convertNumbersToSubscribersMap(subscribersNumbers)
  const { currentUser } = useCurrentUser()
  if (currentUser.value?.id) {
    subscribers.set(currentUser.value.id, currentUser.value as Contact)
  }
  return subscribers
}
